import React, { useReducer, useState, type SubmitEvent } from "react";

import { Box } from "ui/box";
import { Stack } from "ui/stack";

import { useDisclosure } from "hooks/use-disclosure";
import * as zod from "zod";

import { TextField } from "ui/text-field";
import { FormGroup, Form } from "ui/forms";

import { Button } from "ui/button";
import FormErrors from "components/partials/form-errors/form-errors";
import { MaskedInput } from "components/partials/masked-input/masked-input";
import { ConfirmationDialog } from "ui/confirmation-dialog";

import { useCurrentUser } from "hooks/api/use-current-user";
import { ClientAttributes, ClientWriteBody } from "hooks/api/use-clients";

import {
  deeplyTransformEmptyStringToUndefined,
  convertUrlToHttps,
  inputIsAlphanumeric,
  inputIsLowercase,
} from "utilities";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";
import { ZodFormErrors } from "types";
import { Formify } from "types/utility";

interface CreateClientFormProps {
  client?: ClientAttributes;
  onSubmit: (attributes: ClientWriteBody) => void;
  onCancel: () => void;
}

interface UpdateClientFormProps {
  client: ClientAttributes;
  onSubmit: (attributes: ClientWriteBody & { id: string }) => void;
  onCancel: () => void;
}

type ClientFormProps = CreateClientFormProps | UpdateClientFormProps;

function isUpdatingClient(props: ClientFormProps): props is UpdateClientFormProps {
  return (props as UpdateClientFormProps).client !== undefined;
}

function BasicInfo(props: ClientFormProps) {
  const form = useClientFormState(props.client);
  const currentUser = useCurrentUser();
  const confirmCancelModal = useDisclosure();

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();

    const { apolloDomainUrl, collegeUrl, privacyPolicyUrl } = form.values;

    try {
      const clientAttributes = schema.parse(
        deeplyTransformEmptyStringToUndefined({
          ...form.values,
          apolloDomainUrl: apolloDomainUrl && convertUrlToHttps(apolloDomainUrl),
          collegeUrl: collegeUrl && convertUrlToHttps(collegeUrl),
          privacyPolicyUrl: privacyPolicyUrl && convertUrlToHttps(privacyPolicyUrl),
        }),
      );

      form.setErrors({});

      const clientWithTeam = {
        ...clientAttributes,
      };

      if (isUpdatingClient(props)) {
        props.onSubmit({ ...(clientWithTeam as ClientWriteBody), id: props.client.id });
      } else {
        props.onSubmit(clientWithTeam as ClientWriteBody);
      }
    } catch (error) {
      if (error instanceof zod.ZodError) {
        form.setErrors(error.flatten().fieldErrors as ZodFormErrors);
      }
    }
  }

  return (
    <Box id="client-form" data-testid="client-form">
      {Object.entries(form.errors).length > 0 && <FormErrors form={form} />}

      <Form
        onSubmit={handleSubmit}
        data-testid={props.client ? "edit-client-form" : "new-client-form"}>
        <FormGroup>
          <TextField
            required
            id="client-name"
            data-testid="client-name-input"
            label="Client name"
            value={form.values.name}
            onChange={form.handleChange("name")}
          />
          <TextField
            required
            id="program-iterable-id"
            label="Client Iterable project ID"
            value={form.values.iterableProjectId}
            onChange={form.handleChange("iterableProjectId")}
            helperText="Please enter unique code for this client. This will be used to map campaign creative to Iterable workflows."
          />
          <TextField
            required
            id="client-code"
            data-testid="client-prefix-input"
            label="Client prefix"
            helperText="Please select unique 2–4 digit code for this client. This will be prefix for content IDs for this client."
            value={form.values.clientCode}
            onChange={form.handleChange("clientCode")}
            slotProps={{ input: { maxLength: 4 } }}
          />
          {props.client && (
            <MaskedInput
              id="iterable-api-key"
              name="iterable-api-key"
              data-testid="iterable-api-key-input"
              label="Iterable API key"
              helperText="Please enter API key required to connect this client to associated Iterable project for management of campaign workflows."
              isLocked={!!props.client?.iterableApiKey}
              incomingValue={props.client?.iterableApiKey}
              clientId={props.client.id}
            />
          )}
          <TextField
            disabled={!hasPermission(currentUser, Permission.PERM_CLIENT_ADVANCED)}
            id="Apollo-profile-code"
            data-testid="Apollo-profile-code-input"
            label="Apollo profile code"
            helperText="Please enter unique code for this client using all lowercase characters with no spaces. This will be used to capture user events using Tealium scripts and identify the CDN storage directory for landing pages."
            value={form.values.tealiumProfileCode}
            onChange={form.handleChange("tealiumProfileCode")}
          />
          <TextField
            required
            id="apollo-domain-url"
            data-testid="client-domain-url-input"
            label="Client apollo URL"
            helperText="Please include full address starting with https:// for program digital assets."
            value={form.values.apolloDomainUrl}
            onChange={form.handleChange("apolloDomainUrl")}
          />
          <TextField
            id="client-url"
            data-testid="client-url-input"
            label="Client website URL"
            helperText="Please include full address starting with https://."
            value={form.values.collegeUrl}
            onChange={form.handleChange("collegeUrl")}
          />
          <TextField
            id="client-privacy-url"
            data-testid="client-privacy-url-input"
            label="Client privacy policy URL"
            helperText="Please include full address starting with https://."
            value={form.values.privacyPolicyUrl}
            onChange={form.handleChange("privacyPolicyUrl")}
          />
        </FormGroup>

        <Stack direction="row" spacing="0.5rem" sx={{ alignItems: "center" }}>
          <Button size="large" data-testid="client-form-submit-button" type="submit">
            {isUpdatingClient(props) ? "Update client" : "Create client"}
          </Button>
          <Button
            size="large"
            data-testid="client-form-cancel-button"
            variant="text"
            onClick={confirmCancelModal.onOpen}>
            Cancel
          </Button>
        </Stack>
        <ConfirmationDialog
          open={confirmCancelModal.isOpen}
          onClose={confirmCancelModal.onClose}
          headline="Leave without saving?"
          message="All unsaved changes will be lost."
          cancelButtonText="Continue editing"
          confirmButtonText="Leave setup"
          onConfirm={props.onCancel}
          intent="warning"
        />
      </Form>
    </Box>
  );
}

export default BasicInfo;

const schema = zod.object({
  clientCode: zod.string().transform((val) => val.toUpperCase()),
  name: zod.string(),
  tealiumProfileCode: zod.string().refine(
    (val) => inputIsAlphanumeric(val) && inputIsLowercase(val) && !val.includes(" "),
    (val) => ({
      message: val.includes(" ")
        ? "The Client Apollo profile code cannot contain spaces - please remove them."
        : "The Client Apollo profile code must only include lower-case alphanumeric characters - please correct.",
    }),
  ),

  iterableProjectId: zod.string().optional(),
  privacyPolicyUrl: zod.string().optional(),
  collegeUrl: zod.string().optional(),
  apolloDomainUrl: zod.string().optional(),
});
type ClientAttributesSchema = zod.infer<typeof schema>;
type ClientAttributesFormState = Formify<ClientAttributesSchema>;
type ClientAttributesFormErrors = Partial<{ [k in keyof ClientAttributesSchema]: string }>;

function initialClientFormValues(client?: ClientAttributes): ClientAttributesFormState {
  // prettier-ignore
  return {
    clientCode: client?.clientCode?.toString() || '',
    name:       client?.name || '',
    tealiumProfileCode: client?.tealiumProfileCode || '',

    privacyPolicyUrl: client?.privacyPolicyUrl || '',
    collegeUrl:          client?.collegeUrl || '',
    iterableProjectId: client?.iterableProjectId || '',
    apolloDomainUrl: client?.apolloDomainUrl || '',
  }
}

function useClientFormState(client?: ClientAttributes) {
  const [values, dispatch] = useReducer(
    function reducer(state: ClientAttributesFormState, action: { field: string; value: string }) {
      switch (action.field) {
        case "name":
        case "privacyPolicyUrl":
        case "collegeUrl":
        case "iterableProjectId":
        case "tealiumProfileCode":
        case "apolloDomainUrl":
          return { ...state, [action.field]: action.value };

        case "clientCode":
          return { ...state, clientCode: action.value.toUpperCase() };

        default:
          throw new Error();
      }
    },
    client,
    initialClientFormValues,
  );

  function handleChange(field: string) {
    return function (event: React.ChangeEvent<HTMLInputElement>) {
      dispatch({ field, value: event.target.value });
    };
  }

  const [errors, setErrors] = useState<ClientAttributesFormErrors>({});

  function handleErrors(errors: ZodFormErrors): void {
    const invalidFields = Object.keys(errors) as Array<keyof ClientAttributesSchema>;
    setErrors(
      invalidFields.reduce((memo, field) => {
        memo[field] = errors[field].join(", ");
        return memo;
      }, {} as ClientAttributesFormErrors),
    );
  }

  return { values, handleChange, errors, setErrors: handleErrors };
}
